import React, { useState } from 'react';
import {
  Typography,
  Button,
  Card,
  Row,
  Col,
  Space,
  Descriptions,
  Tabs,
  List,
  Form,
  message,
  Skeleton,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import type { Lead, Contact } from '../../types';
import LeadModal from '../../components/sales/LeadModal';
import ConfirmDeleteModal from '../../components/modal/ConfirmDeleteModal';
import ContactEditModal from '../../components/modal/ContactEditModal';
import { getLeadById, deleteLead, updateLeadContacts } from '../../api/leads';
import LeadStatusTag from '../../components/sales/LeadStatusTag';
import { formatDateLocale } from '../../utils/date';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_STALE_TIME_MS } from '../../constants/api';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Utility function to convert URLs in text to clickable links
const formatTextWithLineBreaks = (text: string): React.ReactNode => {
  if (!text) return text;

  // URL regex pattern that matches http/https URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  
  const linkifyText = (text: string): React.ReactNode => {
    const parts = text.split(urlRegex);
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        return (
          <a
            key={index}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1890ff', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return text.split('\n').map((line, index, array) => (
    <React.Fragment key={index}>
      {linkifyText(line)}
      {index < array.length - 1 && <br />}
    </React.Fragment>
  ));
};

const LeadDetailsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const lang = i18n.language || 'en-US';

  // Use React Query for lead details
  const {
    data: lead,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['lead', id],
    queryFn: () => getLeadById(id!),
    enabled: !!id,
    initialData: location.state?.lead,
    select: (data) => {
      if (!data) return null;
      // Add clientIds to existing contacts if they don't have one
      return {
        ...data,
        contacts: data.contacts.map((contact: Contact) => ({
          ...contact,
          clientId: contact.clientId || crypto.randomUUID()
        }))
      };
    },
    staleTime: API_STALE_TIME_MS,
  });

  const queryClient = useQueryClient();
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteLead(id),
    onSuccess: () => {
      message.success(t('success:deleteSuccess', { entity: t('leads:lead') }));
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      navigate('/sales/leads');
    },
    onError: () => {
      message.error(t('sales:errors.deleteFailed', { entity: t('leads:lead') }));
    },
  });

  const [activeTab, setActiveTab] = useState('1');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [form] = Form.useForm();
  const [openContactModal, setOpenContactModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [openDeleteContactDialog, setOpenDeleteContactDialog] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<Contact | null>(null);
  const contactTypeOptions = [
    { value: 'primary', label: 'Primary' },
    { value: 'secondary', label: 'Secondary' },
    { value: 'billing', label: 'Billing' },
    { value: 'technical', label: 'Technical' }
  ];

  const updateContactsMutation = useMutation({
    mutationFn: ({ id, contacts }: { id: string, contacts: Contact[] }) => updateLeadContacts(id, contacts),
    onSuccess: () => {
      message.success(t('success:saveSuccess', { entity: t('contacts:contact') }));
      queryClient.invalidateQueries({ queryKey: ['lead', id] });
      setOpenContactModal(false);
      setEditingContact(null);
    },
    onError: (error) => {
      console.error('Failed to update contacts:', error);
      message.error(t('sales:errors.saveFailed', { entity: t('contacts:contact') }));
    }
  });

  if (isLoading) {
    return <div style={{ padding: 32 }}><Card><Skeleton active /></Card></div>;
  }
  if (isError || !lead) {
    return <div style={{ padding: 32 }}><Card><Typography.Text type="danger">{t('errors:notFound', { entity: t('sales:leads.lead') })}</Typography.Text></Card></div>;
  }

  const handleBackToList = () => {
    navigate('/sales/leads');
  };

  const handleEdit = () => {
    setEditingLead(lead);
    form.setFieldsValue({
      name: lead.name,
      status: lead.status,
      source: lead.source,
      address: lead.address,
      city: lead.city,
      pincode: lead.pincode,
      country: lead.country,
      description: lead.description,
      notes: lead.notes,
      contacts: lead.contacts.map((contact: Contact) => ({
        name: contact.name,
        position: contact.position,
        email: contact.email,
        phone: contact.phone,
        type: contact.type,
      })),
    });
    setOpenDialog(true);
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (lead?.id) {
      deleteMutation.mutate(lead.id);
    }
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setOpenContactModal(true);
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setOpenContactModal(true);
  };

  const handleDeleteContact = (contact: Contact) => {
    setContactToDelete(contact);
    setOpenDeleteContactDialog(true);
  };

  const handleConfirmDeleteContact = () => {
    if (!lead || !contactToDelete) return;
    
    // Use a combination of fields to identify the contact
    const updatedContacts = lead.contacts.filter((c: Contact) => 
      c.name !== contactToDelete.name || 
      c.email !== contactToDelete.email || 
      c.phone !== contactToDelete.phone
    );
    
    updateContactsMutation.mutate({ id: lead.id, contacts: updatedContacts });
    setOpenDeleteContactDialog(false);
    setContactToDelete(null);
  };

  const handleSaveContact = (newContacts: Contact[]) => {
    if (!lead) return;
    try {
      let updatedContacts: Contact[];
      
      if (editingContact) {
        // Update existing contact
        updatedContacts = lead.contacts.map((contact: Contact) => 
          contact.clientId === editingContact.clientId 
            ? { ...newContacts[0], clientId: contact.clientId }
            : contact
        );
      } else {
        // Add new contacts with client IDs
        const contactsWithIds = newContacts.map(contact => ({
          ...contact,
          clientId: crypto.randomUUID()
        }));
        updatedContacts = [...lead.contacts, ...contactsWithIds];
      }

      updateContactsMutation.mutate({ id: lead.id, contacts: updatedContacts });
    } catch (error) {
      message.error(t('sales:errors.saveFailed', { entity: t('contacts:contact') }));
    }
  };

  return (
    <div style={{ width: '100%' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space align="center">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBackToList} />
            <Title level={3} style={{ margin: 0 }}>
              {t('sales:leads.detailsTitle', 'Lead Details')}
            </Title>
          </Space>
        </Col>
        <Col>
          <Space>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:edit', 'Edit')}
            </Button>
            <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
              {t('common:delete', 'Delete')}
            </Button>
          </Space>
        </Col>
      </Row>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab={t('common:details', 'Details')} key="1">
          <Card style={{ marginBottom: 24 }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t('sales:properties.table.name')}>{lead.name}</Descriptions.Item>
              <Descriptions.Item label={t('sales:leads.table.status')}><LeadStatusTag status={lead.status} /></Descriptions.Item>
              <Descriptions.Item label={t('sales:leads.table.source')}>{lead.source}</Descriptions.Item>
              <Descriptions.Item label={t('sales:properties.table.description')}>{lead.description || t('common:none')}</Descriptions.Item>
              <Descriptions.Item label={t('common:notes')}>
                {lead.notes ? formatTextWithLineBreaks(lead.notes) : t('common:none')}
              </Descriptions.Item>
              <Descriptions.Item label={t('common:createdAt')}>{formatDateLocale(lead.created_at, lang)}</Descriptions.Item>
              <Descriptions.Item label={t('common:updatedAt')}>{formatDateLocale(lead.updated_at, lang)}</Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>
        <TabPane tab={t('common:contacts', 'Contacts')} key="2">
          <Card
            title={
              <Row justify="space-between" align="middle">
                <Col>
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddContact}>
                    {t('common:addContact', 'Add Contact')}
                  </Button>
                </Col>
              </Row>
            }
          >
            <List
              dataSource={lead.contacts}
              renderItem={(contact: Contact) => (
                <List.Item
                  actions={[
                    <Button type="link" onClick={() => handleEditContact(contact)}>
                      {t('common:edit', 'Edit')}
                    </Button>,
                    <Button type="link" danger onClick={() => handleDeleteContact(contact)}>
                      {t('common:delete', 'Delete')}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={contact.name}
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary">{contact.position}</Text>
                        <Text type="secondary">{contact.email}</Text>
                        <Text type="secondary">{contact.phone}</Text>
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </TabPane>
        <TabPane tab={t('common:location', 'Location')} key="3">
          <Card title={t('common:location')} style={{ marginBottom: 24 }}>
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t('common:address')}>{lead.address}</Descriptions.Item>
              <Descriptions.Item label={t('common:city')}>{lead.city}</Descriptions.Item>
              <Descriptions.Item label={t('common:pincode')}>{lead.pincode}</Descriptions.Item>
              <Descriptions.Item label={t('common:country')}>{lead.country}</Descriptions.Item>
            </Descriptions>
          </Card>
        </TabPane>
      </Tabs>
      <ConfirmDeleteModal
        open={openDeleteDialog}
        title={t('common:confirmDelete', 'Confirm Delete')}
        description={t('sales:leads.deleteConfirm', 'Are you sure you want to delete this lead? This action cannot be undone.')}
        onOk={handleConfirmDelete}
        onCancel={() => setOpenDeleteDialog(false)}
        okText={t('common:delete', 'Delete')}
        cancelText={t('common:cancel', 'Cancel')}
      />
      <ConfirmDeleteModal
        open={openDeleteContactDialog}
        title={t('common:confirmDelete', 'Confirm Delete')}
        description={t('contacts:deleteConfirm', 'Are you sure you want to delete this contact? This action cannot be undone.')}
        onOk={handleConfirmDeleteContact}
        onCancel={() => {
          setOpenDeleteContactDialog(false);
          setContactToDelete(null);
        }}
        okText={t('common:delete', 'Delete')}
        cancelText={t('common:cancel', 'Cancel')}
        okButtonProps={{ danger: true }}
      />
      <LeadModal
        open={openDialog}
        lead={editingLead}
        onSuccess={() => {
          refetch();
          setOpenDialog(false);
          setEditingLead(null);
        }}
        onCancel={() => {
          setOpenDialog(false);
          setEditingLead(null);
        }}
      />
      <ContactEditModal
        open={openContactModal}
        onSave={handleSaveContact}
        onCancel={() => {
          setOpenContactModal(false);
          setEditingContact(null);
        }}
        contact={editingContact || undefined}
        contactTypeOptions={contactTypeOptions}
        t={t}
        loading={updateContactsMutation.isPending}
        disabled={updateContactsMutation.isPending}
      />
    </div>
  );
};

export default LeadDetailsPage; 